/**
 * Earnings Chart Component
 * Displays historical earnings trend using Recharts
 */
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import type { EarningsDataPoint } from '../../types/botMonitoring'
import { formatCurrency } from '../../utils/formatters'
import LoadingSpinner from '../Common/LoadingSpinner'
import EmptyState from '../Common/EmptyState'

interface EarningsChartProps {
  data: EarningsDataPoint[]
  currency: string
  isLoading?: boolean
  error?: Error | null
  onDaysChange: (days: number) => void
  currentDays: number
}

export default function EarningsChart({
  data,
  currency,
  isLoading,
  error,
  onDaysChange,
  currentDays,
}: EarningsChartProps) {
  // Loading state
  if (isLoading) {
    return <LoadingSpinner message="載入收益圖表..." size="lg" />
  }

  // Error state
  if (error) {
    return (
      <EmptyState
        icon={
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
        title="載入失敗"
        description="無法載入收益圖表，請稍後再試"
      />
    )
  }

  // Empty state
  if (data.length === 0) {
    return (
      <EmptyState
        title="資料收集中"
        description="尚未有足夠的歷史資料，請稍後再查看"
        icon={
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        }
      />
    )
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const point = payload[0].payload as EarningsDataPoint
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-2">{point.date}</p>
          <div className="space-y-1">
            <p className="text-sm text-gray-600">
              累積收益: <span className="font-medium text-green-600">
                {formatCurrency(point.cumulative_earnings, currency)}
              </span>
            </p>
            <p className="text-sm text-gray-600">
              當日收益: <span className="font-medium text-blue-600">
                {formatCurrency(point.daily_earnings, currency)}
              </span>
            </p>
            <p className="text-sm text-gray-600">
              平均利率: <span className="font-medium text-purple-600">
                {(point.avg_rate * 100).toFixed(4)}% / 日
              </span>
            </p>
            <p className="text-sm text-gray-600">
              貸款數量: <span className="font-medium">{point.loan_count}</span>
            </p>
          </div>
        </div>
      )
    }
    return null
  }

  const timeRanges = [
    { label: '7 天', days: 7 },
    { label: '30 天', days: 30 },
    { label: '全部', days: 90 },
  ]

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">收益趨勢</h3>
        <div className="flex gap-2">
          {timeRanges.map((range) => (
            <button
              key={range.days}
              onClick={() => onDaysChange(range.days)}
              className={`
                px-4 py-2 text-sm font-medium rounded-md transition-colors
                ${
                  currentDays === range.days
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => {
              // Format date as MM/DD
              const date = new Date(value)
              return `${date.getMonth() + 1}/${date.getDate()}`
            }}
          />
          <YAxis
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => formatCurrency(value, currency)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            formatter={(value) => {
              const labels: Record<string, string> = {
                cumulative_earnings: '累積收益',
                daily_earnings: '當日收益',
              }
              return labels[value] || value
            }}
          />
          <Line
            type="monotone"
            dataKey="cumulative_earnings"
            stroke="#10b981"
            strokeWidth={2}
            dot={{ fill: '#10b981', r: 4 }}
            activeDot={{ r: 6 }}
            name="cumulative_earnings"
          />
          <Line
            type="monotone"
            dataKey="daily_earnings"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ fill: '#3b82f6', r: 4 }}
            activeDot={{ r: 6 }}
            name="daily_earnings"
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">總累積收益</div>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(data[data.length - 1]?.cumulative_earnings || 0, currency)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">平均每日收益</div>
          <div className="text-2xl font-bold text-blue-600">
            {formatCurrency(
              data.reduce((sum, d) => sum + d.daily_earnings, 0) / data.length || 0,
              currency
            )}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">總貸款數</div>
          <div className="text-2xl font-bold text-gray-900">
            {data.reduce((sum, d) => sum + d.loan_count, 0)}
          </div>
        </div>
      </div>
    </div>
  )
}
